// src/components/modals/ViewAttendanceModal.jsx
import { X, Calendar, Clock, User, Building2, UserCheck, Timer, MapPin, Smartphone } from 'lucide-react'
import { format, parseISO, differenceInMinutes } from 'date-fns'

const ViewAttendanceModal = ({ isOpen, onClose, attendance }) => {
  if (!isOpen || !attendance) return null

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'PPP') // e.g., "January 1st, 2023"
    } catch {
      return dateString
    }
  }

  const formatTime = (timeString) => {
    if (!timeString) return 'Not recorded'
    
    try {
      // Handle both time format (HH:MM) and datetime format
      if (timeString.includes('T') || timeString.includes(' ')) {
        return format(parseISO(timeString), 'h:mm a')
      } else {
        // Simple HH:MM format
        const [hours, minutes] = timeString.split(':')
        const date = new Date()
        date.setHours(parseInt(hours), parseInt(minutes))
        return format(date, 'h:mm a')
      }
    } catch {
      return timeString
    }
  }

  const calculateHoursWorked = (clockIn, clockOut) => {
    if (!clockIn || !clockOut) return 'Not calculated'

    try {
      let clockInTime, clockOutTime

      // Handle different time formats
      if (clockIn.includes('T') || clockIn.includes(' ')) {
        clockInTime = parseISO(clockIn)
        clockOutTime = parseISO(clockOut)
      } else {
        // Simple HH:MM format - use today's date
        const today = format(new Date(), 'yyyy-MM-dd')
        clockInTime = parseISO(`${today}T${clockIn}:00`)
        clockOutTime = parseISO(`${today}T${clockOut}:00`)
      }

      const minutes = differenceInMinutes(clockOutTime, clockInTime)
      const hours = Math.floor(minutes / 60)
      const remainingMinutes = minutes % 60

      if (hours === 0) {
        return `${remainingMinutes} minutes`
      } else if (remainingMinutes === 0) {
        return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
      } else {
        return `${hours}h ${remainingMinutes}m`
      }
    } catch {
      return 'Unable to calculate'
    }
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      present: { bg: 'bg-green-100', text: 'text-green-800', label: 'Present' },
      absent: { bg: 'bg-red-100', text: 'text-red-800', label: 'Absent' },
      on_leave: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'On Leave' }
    }

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status || 'Unknown' }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Attendance Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Employee Info */}
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <User className="h-5 w-5 text-gray-400 mt-0.5" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Employee</p>
              <p className="text-gray-600">{attendance.employee?.fullName || 'Unknown Employee'}</p>
            </div>
          </div>

          {/* Branch Info */}
          {attendance.branch && (
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Building2 className="h-5 w-5 text-gray-400 mt-0.5" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Branch</p>
                <p className="text-gray-600">{attendance.branch.name}</p>
              </div>
            </div>
          )}

          {/* Date */}
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Date</p>
              <p className="text-gray-600">{formatDate(attendance.date)}</p>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <UserCheck className="h-5 w-5 text-gray-400 mt-0.5" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Status</p>
              <div className="mt-1">{getStatusBadge(attendance.status)}</div>
            </div>
          </div>

          {/* Clock Times - Only show if status is present */}
          {(attendance.status === 'present' || attendance.status === 'late') && (
            <>
              {/* Clock-in */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                </div>
                <div className="flex-grow">
                  <p className="text-sm font-medium text-gray-900">Clock-in</p>
                  <p className="text-gray-600">{formatTime(attendance.clockIn)}</p>
                  {attendance.onLocation && (
                    <div className="mt-1.5 flex flex-col space-y-1 text-xs text-gray-500 bg-gray-50 p-2 rounded border border-gray-100">
                      <div className="flex items-center space-x-1.5">
                        <MapPin className="h-3 w-3 text-red-500 flex-shrink-0" />
                        <span><strong>Location:</strong> {attendance.onLocation}</span>
                      </div>
                      {attendance.onMobileId && (
                        <div className="flex items-center space-x-1.5">
                          <Smartphone className="h-3 w-3 text-blue-500 flex-shrink-0" />
                          <span><strong>Device:</strong> {attendance.onMobileId}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Clock-out */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                </div>
                <div className="flex-grow">
                  <p className="text-sm font-medium text-gray-900">Clock-out</p>
                  <p className="text-gray-600">{formatTime(attendance.clockOut)}</p>
                  {attendance.offLocation && (
                    <div className="mt-1.5 flex flex-col space-y-1 text-xs text-gray-500 bg-gray-50 p-2 rounded border border-gray-100">
                      <div className="flex items-center space-x-1.5">
                        <MapPin className="h-3 w-3 text-red-500 flex-shrink-0" />
                        <span><strong>Location:</strong> {attendance.offLocation}</span>
                      </div>
                      {attendance.offMobileId && (
                        <div className="flex items-center space-x-1.5">
                          <Smartphone className="h-3 w-3 text-blue-500 flex-shrink-0" />
                          <span><strong>Device:</strong> {attendance.offMobileId}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Hours Worked */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <Timer className="h-5 w-5 text-gray-400 mt-0.5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Hours Worked</p>
                  <p className="text-gray-600">{calculateHoursWorked(attendance.clockIn, attendance.clockOut)}</p>
                </div>
              </div>
            </>
          )}

          {/* Additional Notes for absent or on leave */}
          {(attendance.status === 'absent' || attendance.status === 'on_leave') && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 text-center">
                {attendance.status === 'absent' 
                  ? 'Employee was marked as absent for this date.'
                  : 'Employee was on leave for this date.'
                }
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ViewAttendanceModal
